<template>
  <fp-field :keys="$props">
    <v-combobox
      :clearable="false"
      :disabled="disabled"
      :label="label"
      :items="timeList"
      :maxlength="mask.length"
      :dense="dense"
      :required="required"
      :item-value="itemKey === false ? null : itemKey"
      :item-text="itemName"
      :hide-details="hideDetails"
      :class="{'fp-input-outline': outline}"
      :outlined="outline"
      :search-input.sync="searchInput"
      :error="isError"
      v-model="model">
      <template slot="item" slot-scope="data">
        <v-list-item-content v-text="data.item"/>
      </template>
    </v-combobox>
  </fp-field>
</template>

<script>
import {Vue} from 'vue-property-decorator';
import {DateFormat} from '@/components/fpComponents/enums/FpConfigEnums';
import moment from 'moment';
import FpField from '@/components/fpComponents/FpField/FpField.vue';
import {VListItemContent, VCombobox} from 'vuetify/lib';
import {conformToMask} from 'text-mask-core';

const HOUR_FORMAT = 'hh:mm';

const Patterns = {
  '#': { pattern: /\d/, },
  '*': { pattern: /[0-9a-zA-Z]/, },
  '@': { pattern: /[a-zA-Z]/, },
};

export default Vue.extend({
  name: 'FpTimepicker',
  components: {FpField, VCombobox, VListItemContent,},
  props: {
    value: {
      type: Date,
      default: undefined,
    },
    outline: {
      default: true,
      type: Boolean,
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    mask: {
      type: String,
      default: '##:##',
    },
    itemName: {
      type: String,
      default: 'name',
    },
    itemKey: {
      type: String,
      default: 'value',
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    description: {
      type: String,
      default: undefined,
    },
    label: {
      type: String,
      default: undefined,
    },
    maxHour: {
      type: String,
      default: undefined,
    },
    minHour: {
      type: String,
      default: undefined,
    },
    isError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchValue: '',
    };
  },
  methods: {
    emitValue(v) {
      let newDate = this.value
        ? (this.value instanceof Date ? this.value : new Date(this.value))
        : new Date();
      const time = [v.slice(0,2), v.slice(3,5),];
      if (time[0] && time[0].length === 2 && time[1] && time[1].length === 2) {
        newDate.setHours(Number(time[0]));
        newDate.setMinutes(Number(time[1]));
        newDate.setSeconds(0);
        if (this.returnType === DateFormat.ISO_OBJECT) {
          newDate = newDate.toISOString();
        }
        this.$emit('input', newDate);
      }
    },
    isHourGreaterThanMin(hour, minutes) {
      const minHourNumberHours = moment(this.minHour, HOUR_FORMAT).hour();
      const minHourNumberMinutes = moment(this.minHour, HOUR_FORMAT).minutes();
      if (hour === minHourNumberHours) {
        return minutes > minHourNumberMinutes;
      } else {
        return hour > minHourNumberHours;
      }
    },
    isHourSmallerThanMax(hour, minutes) {
      const maxHourNumberHours = moment(this.maxHour, HOUR_FORMAT).hour();
      const maxHourNumberMinutes = moment(this.maxHour, HOUR_FORMAT).minutes();
      if (hour === maxHourNumberHours) {
        return minutes < maxHourNumberMinutes;
      } else {
        return hour < maxHourNumberHours;
      }
    },
  },
  computed: {
    searchInput: {
      get() {
        return this.searchValue;
      },
      set(nv) {
        if (nv) {
          if (nv instanceof Date) {
            nv = nv.toISOString();
          }
          const mask = this.mask.split('').map(x => Patterns[x] ? Patterns[x].pattern : x);
          let maskedValue = conformToMask(nv, mask, {guide: false,})
            .conformedValue
            .substr(0, this.mask.length);
          if (maskedValue.endsWith(this.mask[maskedValue.length - 1])) {
            maskedValue = maskedValue.substr(0, maskedValue.length - 1);
          }
          if (maskedValue.length === this.mask.length) {
            this.emitValue(maskedValue);
          }
          this.searchValue = maskedValue;
        }
      },
    },
    timeList() {
      return (new Array(24)).fill(0).flatMap((x, index) => {
        const hour = index < 10 ? `0${index}` : String(index);
        return [`${hour}:00`,`${hour}:30`,];
      }).filter(x => {
        const hour = moment(x, HOUR_FORMAT).hour();
        const minutes = moment(x, HOUR_FORMAT).minutes();
        if (this.minHour && this.maxHour) {
          return this.isHourGreaterThanMin(hour, minutes) && this.isHourSmallerThanMax(hour, minutes);
        } else if (this.minHour) {
          return this.isHourGreaterThanMin(hour, minutes);
        } else if (this.maxHour) {
          return this.isHourSmallerThanMax(hour, minutes);
        } else {
          return true;
        }
      });
    },
    model: {
      get() {
        const value = this.value;
        if (value && value !== '') {
          return moment(value instanceof Date ? value : new Date(value)).format('HH:mm');
        } else {
          return null;
        }
      },
      set(newValue) {
        this.emitValue(newValue);
      },
    },
  },
});
</script>

<style scoped lang="scss">
  .edit-input{
    cursor: pointer;
    font-size: 18px;
    line-height: 1.44;
    font-weight: 500;
    i {
      padding-left: 8px;
    }
  }
  >>> input {
    text-align: right;
  }
  .confirm-input {
    width: fit-content
  }
  .remove-top-spaces {
    & >>> div.v-input {
    margin-top: 0;
    padding-top: 0;
  }
  }
</style>
